import { defineStore } from 'pinia';
import { useNuxtApp } from 'nuxt/app';
import { computed, ref } from 'vue';
import { useAuchanStoresStore } from '~~/dealer/app/stores/auchanStore';
import { setErrorNotification } from '~~/common/utils/helper';
import type { ResponseDataDealer } from '~~/common/types/api';

type StoresResponse = ResponseDataDealer<'/api/v2/stores', 'get'>;

// NOTE: hand-written types — the generated API description of the stock_infos
// endpoints is wrong (stockInfoByAllStore items actually have storeCode /
// productId / storeStock / storeLogisticStock).
interface StockInfoByStore {
  storeCode: string;
  productId: number;
  storeStock: number;
  storeLogisticStock: number;
}

interface ProductStockInfo {
  productId: number;
  status: string;
  storeStock: number;
  logisticStock: number;
  logisticGlobalStock: number;
  /** Empty when the request is made with `details` falsy, filled otherwise. */
  stockInfoByAllStore: StockInfoByStore[];
}

interface GlobalStock {
  logisticGlobalStock: number;
  stockStatus: string;
}

interface StockBranch {
  stock: number;

  defaultDeliveryMode(shipmentMethod: string): string;

  token: string;

  isDisabled(predictedStock?: number): boolean;

  icon: {
    name: string;
    size: number;
    fill: boolean;
  };
}

interface StockBranches {
  store: StockBranch;
  logistic: StockBranch;
}

let throttleId: ReturnType<typeof setTimeout> | undefined;
let pendingProductIds: number[] = [];

export const useStockInfoStore = defineStore('stockInfo', () => {
  const nuxtApp = useNuxtApp();
  const auchanStore = useAuchanStoresStore();

  const gStock = ref<Record<number/*productId*/, GlobalStock>>({});
  const lStock = ref<Record<number/*productId*/, Record<string/*storeId*/, StockBranches>>>({});

  function setGlobalStock ({ productId, logisticGlobalStock, status: stockStatus = '' }: {
    productId: number;
    logisticGlobalStock: number;
    status?: string;
  }) {
    gStock.value[productId] = { logisticGlobalStock, stockStatus };
  }

  function setLocalStock ({ productId, storeId, stock }: {
    productId: number;
    storeId: string;
    stock: StockBranches;
  }) {
    if (!lStock.value[productId]) {
      lStock.value[productId] = {};
    }
    lStock.value[productId][storeId] = stock;
  }

  function fetchStock ({
    productIds,
    details = false,
  }: {
    productIds: number[];
    details?: boolean;
  }) {
    const { $logger } = nuxtApp;
    try {
      if (details) {
        groupStockRequests({ productIds, details });
        return;
      }
      pendingProductIds = pendingProductIds.concat(productIds);
      clearTimeout(throttleId);
      throttleId = setTimeout(() => {
        groupStockRequests({ productIds: [...pendingProductIds], details });
        pendingProductIds = [];
      }, 300);
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  async function groupStockRequests ({
    productIds,
    details = false,
  }: {
    productIds: number[];
    details?: boolean;
  }) {
    const { $api, $logger } = nuxtApp;

    try {
      if (!Array.isArray(productIds)) {
        throw new TypeError('productIds has to be an array');
      }
      if (!productIds.length) {
        throw new TypeError('productIds has to contain at least one number');
      }
      const isOne = productIds.length === 1;

      async function fetchOneStock () {
        const result = await $api.$get<ProductStockInfo>(`stock_infos/${productIds[0]}/${Number(details)}`);
        return [result];
      }

      async function fetchMoreStocks () {
        return await $api.$get<ProductStockInfo[]>('stock_infos', {
          params: {
            productId: Array.from(new Set(productIds)),
          },
        });
      }

      const [data] = await Promise.all([
        (isOne
          ? fetchOneStock
          : fetchMoreStocks
        )(),
        ((isOne && details)
          ? auchanStore.fetchAuchanStores()
          : undefined
        ),
      ]);
      data.forEach((prodStock) => {
        setGlobalStock(prodStock);
        for (const s of stockGenerator(auchanStore.stores, prodStock)) {
          setLocalStock({ productId: prodStock.productId, storeId: s.storeId, stock: createStockBranches(s) });
        }
      });
    } catch (error) {
      $logger.error(error);
      setErrorNotification({ nuxtApp, error });
    }
  }

  const getGlobalStockInfo = computed(() => (productId: number) => {
    return gStock.value?.[productId] || { logisticGlobalStock: 0 };
  });
  const getLocalStockInfo = computed(() => (productId: number, storeId: string = 'current') => {
    return lStock.value?.[productId]?.[storeId] || createStockBranches({ storeStock: 0, logisticStock: 0 });
  });
  const getFullStockInfo = computed(() => (productId: number, storeId: string = 'current') => {
    const { store, logistic } = getLocalStockInfo.value(productId, storeId);
    return createStockBranches({
      storeStock: store.stock,
      logisticStock: logistic.stock + (gStock.value?.[productId]?.logisticGlobalStock || 0),
    });
  });

  return {
    gStock,
    lStock,
    setGlobalStock,
    setLocalStock,
    fetchStock,
    groupStockRequests,
    getGlobalStockInfo,
    getLocalStockInfo,
    getFullStockInfo,
  };
});

function* stockGenerator (stores: StoresResponse, productsStock: ProductStockInfo) {
  yield {
    storeId: 'current',
    storeStock: productsStock.storeStock,
    logisticStock: productsStock.logisticStock,
  };
  if (productsStock.stockInfoByAllStore && productsStock.stockInfoByAllStore.length) {
    const sibas = productsStock.stockInfoByAllStore.reduce<Record<string/*storeCode*/, StockInfoByStore>>((acc, curr) => {
      acc[curr.storeCode] = curr;
      return acc;
    }, {});
    for (const { id } of stores) {
      if (!id) {
        continue;
      }
      yield {
        storeId: id,
        storeStock: sibas[id]?.storeStock || 0,
        logisticStock: sibas[id]?.storeLogisticStock || 0,
      };
    }
  }
}

function createStockBranches ({
  storeStock,
  logisticStock,
}: {
  storeStock: number;
  logisticStock: number;
}): StockBranches {
  const store = {
    stock: storeStock,
    defaultDeliveryMode (shipmentMethod: string) {
      return shipmentMethod === 'EXT_PALETT' ? 'extra_palett' : 'store';
    },
    token: 'awd.common.stock.types.store',
    isDisabled: () => false,
    icon: {
      name: 'dealer-pick-store-32',
      size: 32,
      fill: true,
    },
  } satisfies StockBranch;
  const logistic = {
    stock: logisticStock,
    defaultDeliveryMode (shipmentMethod: string) {
      return shipmentMethod === 'EXT_PALETT' ? 'extra_palett' : 'home';
    },
    token: 'awd.common.stock.types.logistic',
    isDisabled (this: StockBranch, predictedStock = this.stock) {
      return predictedStock < 0;
    },
    icon: {
      name: 'dealer-pick-logistic-32',
      size: 32,
      fill: true,
    },
  } satisfies StockBranch;
  return { store, logistic };
}
